import { useQuery } from '@tanstack/react-query';
import { NavbarTemplate } from '@/components/template/NavbarTemplate';
import { HeroTemplate } from '@/components/template/HeroTemplate';
import { SecaoSobre } from '@/components/template/SecaoSobre';
import { SecaoDestaques } from '@/components/template/SecaoDestaques';
import { SecaoTransmissao } from '@/components/template/SecaoTransmissao';
import { SecaoTorneios } from '@/components/template/SecaoTorneios';
import { SecaoComunidade } from '@/components/template/SecaoComunidade';
// Oculta na home (ver abaixo); o import volta junto com a seção.
// import { SecaoPlanos } from '@/components/template/SecaoPlanos';
// Oculta na home (ver abaixo); o import volta junto com a seção.
// import { SecaoHighlights } from '@/components/template/SecaoHighlights';
import { SecaoPerguntas } from '@/components/template/SecaoPerguntas';
import {
  SecaoParceiros,
  SecaoDepoimentos,
  SecaoNoticias,
  SecaoMarca,
} from '@/components/template/SecoesEditoriais';
import { RodapeTemplate } from '@/components/template/RodapeTemplate';
import { useGatilhosSempreEmDia } from '@/components/template/animacoes';
import { queries, type TournamentSummary } from '@/lib/api';

/**
 * A home.
 *
 * É a única página do produto desenhada com o layout do template — daí o
 * `.mpc-template` na raiz. Tudo dentro dessa classe obedece à folha portada
 * (`styles/template.css`): a escala do Anton, o container de 77rem, os raios de
 * pílula, os espaçamentos. Fora dela, o resto do app segue o design system do
 * produto, e as duas linguagens não se atrapalham.
 *
 * A ordem das seções é a do template, e ela é a ordem das perguntas de quem
 * chega:
 *
 *   hero          o que é isto, e quando é a próxima
 *   sobre         a frase, os escudos e os números
 *   destaques     quem já está dentro
 *   transmissão   o confronto em destaque, com a campanha dos dois lados
 *   partidas      o calendário da edição, em duas abas
 *   comunidade    por que entrar
 *   planos        como entrar        — oculta a pedido do usuário
 *   parceiros     quem apoia          — some enquanto não houver
 *   depoimentos   quem já jogou       — some enquanto não houver
 *   notícias      o que aconteceu     — some enquanto não houver
 *   vídeo         a competição em movimento — oculta a pedido do usuário
 *   perguntas     as dúvidas práticas
 *   marca         o convite ao patrocinador
 *   rodapé
 *
 * `.page-wrapper` e `.main-wrapper` são do template e existem pelo `overflow:
 * clip`: várias seções têm elementos que estouram a lateral de propósito — o
 * halo do rodapé, os escudos que derivam, os gradientes das fixtures — e é esse
 * recorte que impede a página inteira de ganhar rolagem horizontal por causa
 * deles.
 */
export function HomePage() {
  const { data, isLoading } = useQuery(queries.tournaments('registration'));

  /*
   * Metade das seções só existe depois que a API responde, e a página quase
   * dobra de altura quando isso acontece. Sem recalcular os gatilhos de
   * rolagem, as seções de baixo ficam presas no estado inicial da animação —
   * invisíveis, num layout perfeitamente correto.
   */
  useGatilhosSempreEmDia();

  const abertas: TournamentSummary[] = (data?.tournaments ?? [])
    .slice()
    .sort((a, b) => (a.startsAt ?? Infinity) - (b.startsAt ?? Infinity));

  return (
    <div className="mpc-template">
      <div className="page-wrapper">
        <div className="main-wrapper">
          <NavbarTemplate />
          <HeroTemplate abertas={abertas} carregando={isLoading} />
          <SecaoSobre />
          <SecaoDestaques />
          <SecaoTransmissao />
          <SecaoTorneios />
          <SecaoComunidade />
          {/*
            "Faça parte" (os planos) fica oculta a pedido do usuário. O
            componente segue no projeto: para trazê-la de volta, basta
            descomentar esta linha.
          */}
          {/* <SecaoPlanos /> */}
          <SecaoParceiros />
          <SecaoDepoimentos />
          <SecaoNoticias />
          {/*
            O vídeo em largura cheia ("A competição em movimento") também fica
            oculto a pedido do usuário. Descomentar traz de volta.
          */}
          {/* <SecaoHighlights /> */}
          <SecaoPerguntas />
          <SecaoMarca />
          <RodapeTemplate />
        </div>
      </div>
    </div>
  );
}
